import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { Checkbox } from "@mui/material"
import i18n from "i18n"
import manualPart from "assets/images/budgeting/manualPart.png"
import TextFieldNumber from "common/TextFieldNumber"
import { partPropType } from "../CertHistoryPropTypes"
import {
  borderlessNumberFieldSx,
  getCurrentInvoicing,
  getCurrentPercent,
  normalizeInvoicingAmount,
  normalizeInvoicingPercent,
  getPendingAmount,
} from "./certHistoryRowContentUtils"
import { getPartSelectionKey } from "pages/CertificationsHistory/utils/certificationsHistoryInvoiceDraft"

const CertHistoryRowContentRow = ({
  part,
  index,
  isInvoicing = false,
  selectedParts,
  invoicingDrafts,
  onToggleSelectedPart,
  onUpdateInvoicingDraft,
  onPersistInvoicingAmount,
  onOpenMeasuresDialog,
  numberPrefix = "",
}) => {
  const selectionKey = getPartSelectionKey(part)
  const isSelected = Boolean(selectedParts?.[selectionKey])
  const currentInvoicing = getCurrentInvoicing(part, invoicingDrafts)
  const currentPercent = getCurrentPercent(part, currentInvoicing)

  const [localAmount, setLocalAmount] = useState(currentInvoicing)
  const [localPercent, setLocalPercent] = useState(currentPercent)
  const isEditingRef = useRef(false)

  useEffect(() => {
    if (isEditingRef.current) return
    setLocalAmount(currentInvoicing)
    setLocalPercent(currentPercent)
  }, [currentInvoicing, currentPercent])

  const previousAmountRef = useRef(currentInvoicing)
  const invoicingFieldSx = {
    ...borderlessNumberFieldSx,
    visibility: isSelected ? "visible" : "hidden",
  }
  const hasMeasureDeltaDetails = Boolean(
    part?.cert?.measure_delta_details?.length,
  )

  const handleTogglePart = () => {
    onToggleSelectedPart(part)
  }

  const handleInvoicingFocus = () => {
    isEditingRef.current = true
    previousAmountRef.current = getCurrentInvoicing(part, invoicingDrafts)
  }

  const handleAmountBlur = () => {
    isEditingRef.current = false
    const normalized = normalizeInvoicingAmount(part, localAmount)
    setLocalAmount(normalized)
    setLocalPercent(getCurrentPercent(part, normalized))
    onUpdateInvoicingDraft(part, normalized)
    onPersistInvoicingAmount(part, previousAmountRef.current, normalized)
  }

  const handlePercentBlur = () => {
    isEditingRef.current = false
    const normalized = normalizeInvoicingPercent(part, localPercent)
    setLocalAmount(normalized)
    setLocalPercent(getCurrentPercent(part, normalized))
    onUpdateInvoicingDraft(part, normalized)
    onPersistInvoicingAmount(part, previousAmountRef.current, normalized)
  }

  const handleMeasuresClick = e => {
    e.preventDefault()
    e.stopPropagation()

    if (typeof onOpenMeasuresDialog === "function") {
      onOpenMeasuresDialog(part)
    }
  }

  return (
    <tr
      style={{
        backgroundColor: isSelected ? "#eff6ffb3" : "transparent",
      }}
    >
      <td style={{ minWidth: "40px" }}>
        {`${numberPrefix}${(part.position ?? index) + 1}`}
      </td>
      <td
        colSpan="4"
        style={{
          maxWidth: "0px",
          width: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          textAlign: "left",
          paddingLeft: "10px",
          paddingRight: "60px",
          position: "relative",
        }}
      >
        {part.name}
        {hasMeasureDeltaDetails && (
          <div
            className="editarPartidaDiv"
            style={{
              position: "absolute",
              width: "50px",
              right: "0px",
              top: "55%",
              transform: "translateY(-50%)",
            }}
          >
            <img
              style={{
                width: "32px",
                height: "32px",
                cursor: "pointer",
              }}
              className="segundoIconoShow"
              onClick={handleMeasuresClick}
              src={manualPart}
            />
            <div
              className="segundoIcono"
              style={{
                position: "absolute",
                marginLeft: "-46px",
                marginTop: "-2px",
                zIndex: "99",
              }}
            >
              <div className="recuadro">
                <div className="recuadroPico"></div>
                {i18n.t("Ver mediciones")}
              </div>
            </div>
          </div>
        )}
      </td>
      <td style={{ minWidth: "70px" }}>{part.quantity}</td>
      <td style={{ minWidth: "70px" }}>{part.unity}</td>
      <td style={{ minWidth: "70px" }}>{part.clientPrice.toFixed(2)}</td>
      <td style={{ minWidth: "70px" }}>{part.clientTotalPrice.toFixed(2)}</td>
      <td style={{ minWidth: "70px", whiteSpace: "nowrap" }}>
        {part.unitDelta.toFixed(2)} {part.unity}
      </td>
      <td style={{ minWidth: "70px" }}>{part.percentageDelta.toFixed(2)}</td>
      <td style={{ minWidth: "70px" }}>{part.priceDelta.toFixed(2)}</td>
      <td style={{ minWidth: "70px" }}>{part.totalPercentage.toFixed(2)}</td>
      <td style={{ minWidth: "70px", whiteSpace: "nowrap" }}>
        {part.totalUnit.toFixed(2)} {part.unity}
      </td>
      <td style={{ minWidth: "70px" }}>{part.totalPrice.toFixed(2)}</td>
      {isInvoicing && (
        <>
          <td style={{ minWidth: "70px" }}>
            {(part.invoicedAmount ?? 0).toFixed(2)}
          </td>
          <td style={{ minWidth: "70px" }}>
            {getPendingAmount(part).toFixed(2)}
          </td>
          <td style={{ minWidth: "88px" }}>
            <TextFieldNumber
              value={localPercent}
              onChange={value => {
                setLocalPercent(value)
                setLocalAmount(normalizeInvoicingPercent(part, value))
              }}
              onBlur={handlePercentBlur}
              disabled={!isSelected}
              isPercentage
              allowNegative={false}
              variant="filled"
              size="small"
              InputProps={{
                disableUnderline: true,
                onFocus: handleInvoicingFocus,
              }}
              sx={invoicingFieldSx}
            />
          </td>
          <td style={{ minWidth: "88px" }}>
            <TextFieldNumber
              value={localAmount}
              onChange={value => {
                setLocalAmount(value)
                setLocalPercent(
                  getCurrentPercent(
                    part,
                    normalizeInvoicingAmount(part, value),
                  ),
                )
              }}
              onBlur={handleAmountBlur}
              disabled={!isSelected}
              allowNegative={false}
              variant="filled"
              size="small"
              InputProps={{
                disableUnderline: true,
                onFocus: handleInvoicingFocus,
              }}
              sx={invoicingFieldSx}
            />
          </td>
          <td style={{ minWidth: "20px", textAlign: "center" }}>
            <Checkbox
              size="small"
              checked={isSelected}
              onChange={handleTogglePart}
            />
          </td>
        </>
      )}
    </tr>
  )
}

CertHistoryRowContentRow.propTypes = {
  part: partPropType.isRequired,
  index: PropTypes.number.isRequired,
  isInvoicing: PropTypes.bool,
  selectedParts: PropTypes.object.isRequired,
  invoicingDrafts: PropTypes.object.isRequired,
  onToggleSelectedPart: PropTypes.func.isRequired,
  onUpdateInvoicingDraft: PropTypes.func.isRequired,
  onPersistInvoicingAmount: PropTypes.func.isRequired,
  onOpenMeasuresDialog: PropTypes.func,
  numberPrefix: PropTypes.string,
}

export default CertHistoryRowContentRow
